import React from 'react';
import OlOverlay from 'ol/Overlay';
import './forest_data_map_layer_popover.css';
// import OlLayerTile from 'ol/layer/Tile';
// import { NoneType } from 'ol/expr/expression';
// import { CloseOutlined } from '@ant-design/icons';
// import { PinchZoom } from 'ol/interaction';
import ImageWMS from 'ol/source/ImageWMS';
import { toLonLat } from 'ol/proj';

// import { getLayerConfig } from './utils';
import syncFetch from 'sync-fetch';
import { formatTitle } from '../util/TitleHelper';
import { estimateTileValue } from './utils';

// Bookkeeping fields the services attach to every feature - not useful to
// someone clicking on the map.
const HIDDEN_FIELDS =
  /^(objectid|object_id|fid|gid|globalid|global_id|shape|shape_?_?(area|length|leng|len)|geom|the_geom|bbox)$/i;

// "COUNTY_NAME" / "countyName" -> "County name"
const humanizeFieldName = (name) => {
  let label = String(name)
    .replace(/_+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  if (label === label.toUpperCase()) label = label.toLowerCase();
  // Single-band rasters report their value under the band name.
  if (/^(gray index|band ?1)$/i.test(label)) return 'Value';
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Rasters mark empty pixels with a sentinel (float32 min, -9999, ...)
// rather than leaving them blank.
const isNoDataValue = (value) =>
  typeof value === 'number' &&
  (Math.abs(value) >= 1e30 || value === -9999 || value === -32768);

const formatValue = (value) => {
  if (isNoDataValue(value)) return 'No data';
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', { maximumFractionDigits: 3 });
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

// Turns one clicked feature into [{ label, value, isLink }] rows, using the
// ArcGIS field info (aliases, dates, coded values) when the service has it.
const buildRows = (features, fieldInfo) => {
  const rows = [];
  Object.entries(features).forEach(([columnName, rawValue]) => {
    let label = columnName;
    let value = rawValue;
    let info = fieldInfo?.find(
      (e) => e.name === columnName || e.modelName === columnName
    );
    if (info) {
      if (info.alias) label = info.alias;
      if (info.type === 'esriFieldTypeDate' && value) {
        value = new Date(value).toLocaleDateString('en-US', {
          timeZone: 'UTC',
        });
      }
      const coded = info.domain?.codedValues?.find((e) => e.code === value);
      if (coded) value = coded.name;
    }
    if (HIDDEN_FIELDS.test(columnName)) return;
    if (value === null || value === undefined || value === '') return;
    if (typeof value === 'object') return;
    rows.push({
      label:
        info?.alias && info.alias !== columnName
          ? label
          : humanizeFieldName(label),
      value: formatValue(value),
      noData: isNoDataValue(value),
      isLink: typeof value === 'string' && /^https?:\/\//i.test(value),
    });
  });
  // Lead with what the feature is called; the rest keep service order.
  const isName = (row) => /^(name|.* name|title)$/i.test(row.label);
  return [...rows.filter(isName), ...rows.filter((row) => !isName(row))];
};

// Long attribute lists start collapsed to this many rows.
const COLLAPSED_ROW_COUNT = 8;

// [lon, lat] -> "37.41° N, 122.37° W"
const formatLonLat = ([lon, lat]) =>
  `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? 'N' : 'S'}, ` +
  `${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? 'E' : 'W'}`;

const LAYER_PRIORITY = ['ITS:', 'rrk:'];
const compareLayers = (a, b) => {
  const aIndex = LAYER_PRIORITY.findIndex((p) => a.layer_name.includes(p));
  const bIndex = LAYER_PRIORITY.findIndex((p) => b.layer_name.includes(p));
  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
  if (aIndex !== -1) return -1;
  if (bIndex !== -1) return 1;
  return a.layer_name.localeCompare(b.layer_name);
};

class ForestDataMapPopover extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      feature_attributes: [],
      activeTab: 0,
      showAllRows: false,
      clickedLonLat: null,
      selectedDatasets: props.selectedDatasets,
    };
    this.handleClose = this.handleClose.bind(this);
    this.getLayerConfig = this.getLayerConfig.bind(this);
    this.handleTabKeyDown = this.handleTabKeyDown.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.selectTab = this.selectTab.bind(this);
    this.popupRef = React.createRef();
  }

  handleClose(event) {
    event?.preventDefault();
    this.popup.setPosition(undefined);
    return false;
  }

  handleEscape(event) {
    if (event.key === 'Escape') this.handleClose(event);
  }

  // Switching layers or expanding fields can grow the popup past the map
  // edge, so re-pan once it has re-rendered.
  keepPopupInView = () => {
    if (this.popup?.getPosition()) {
      this.popup.panIntoView({ animation: { duration: 250 }, margin: 20 });
    }
  };

  selectTab(index, callback) {
    this.setState({ activeTab: index, showAllRows: false }, () => {
      this.keepPopupInView();
      callback?.();
    });
  }

  // Left/Right/Home/End move between layer tabs (WAI-ARIA tabs pattern).
  handleTabKeyDown(event, count) {
    const { activeTab } = this.state;
    let next;
    if (event.key === 'ArrowRight') next = (activeTab + 1) % count;
    else if (event.key === 'ArrowLeft') next = (activeTab - 1 + count) % count;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    else return;
    event.preventDefault();
    this.selectTab(next, () =>
      document.getElementById(`popup-tab-${next}`)?.focus()
    );
  }

  componentDidMount() {
    // setup popup
    this.popup = new OlOverlay({
      element: this.popupRef.current,
      // Slide the map so a popup opened near an edge isn't cut off.
      autoPan: { animation: { duration: 250 }, margin: 20 },
    });

    let map = this.props.map;
    if (map === undefined) {
      map = this.props.mapRef.current.getMap();
    }
    map.addOverlay(this.popup);

    var thisObject = this;
    map.on('singleclick', async (evt) => {
      // Clicking a zoomed-out count marker zooms in (attachArcGISOverview)
      // rather than asking what's under the click.
      if (
        map.hasFeatureAtPixel(evt.pixel, {
          layerFilter: (layer) => layer.get('arcgis_overview') === 'counts',
        })
      ) {
        return;
      }
      let layers = [];
      map.getLayers().forEach(function (layer) {
        if (
          layer.getProperties().service_url &&
          layer.getProperties().layer_name
        ) {
          layers.push(layer);
        }
      });

      var feature_attributes = [];
      thisObject.setState({
        activeTab: 0,
        showAllRows: false,
        clickedLonLat: toLonLat(evt.coordinate, map.getView().getProjection()),
      });
      const viewResolution = map.getView().getResolution();
      for (var i = 0; i < layers.length; i++) {
        if (layers[i].getVisible()) {
          let item = this.props.selectedDatasets.filter(
            (e) =>
              this.props.getItemKey(e) ===
              layers[i].getProperties().service_url +
                '/' +
                layers[i].getProperties().layer_name
          )[0];

          let gis_service = this.props.getGisService(item);
          // Cached tile layers are pre-rendered images with nothing to
          // query (and no WMS GetFeatureInfo for the branch below to use),
          // so estimate from the clicked pixel's color and the legend.
          if (gis_service.service_type === 'ArcGISTile') {
            const estimate = await estimateTileValue(
              gis_service.service_url,
              layers[i].getData(evt.pixel)
            );
            if (estimate) {
              feature_attributes.push({
                url: gis_service.service_url,
                layer_name: gis_service.layer_name,
                features: { [estimate.label]: estimate.value },
                isEstimate: true,
              });
              thisObject.setState({
                feature_attributes,
              });
              thisObject.popup.setPosition(evt.coordinate);
            }
            continue;
          }
          if (gis_service.service_type === 'ArcGIS') {
            let pixel = evt.pixel;

            const feature = map.forEachFeatureAtPixel(
              pixel,
              function (feature) {
                return feature;
              },
              {
                layerFilter: function (layer) {
                  return (
                    layer.getProperties().service_url +
                      '/' +
                      layer.getProperties().layer_name ===
                    layers[i].getProperties().service_url +
                      '/' +
                      layers[i].getProperties().layer_name
                  );
                },
              }
            );

            if (feature) {
              let information = await fetch(
                `${gis_service.service_url}?f=pjson`
              )
                .then((response) => {
                  return response.json();
                })
                .catch((error) => console.error(error));

              let fieldInfo = information.fields;

              feature_attributes.push({
                url: gis_service.service_url,
                layer_name: gis_service.layer_name,
                features: feature.values_,
                fieldInfo: fieldInfo,
              });
              thisObject.setState({
                feature_attributes,
              });
              thisObject.popup.setPosition(evt.coordinate);
            }
          } else {
            const source = layers[i].getSource();
            let urlsList;
            if (source instanceof ImageWMS) {
              // Special support for boundaries due to the change to ImageWMS
              const singleUrl = source.getUrl?.();
              urlsList = [singleUrl];
            } else {
              urlsList = layers[i].getSource().getUrls();
            }
            const urls = urlsList;
            const layer_name = layers[i].getSource().getParams().LAYERS;
            const url = layers[i]
              .getSource()
              .getFeatureInfoUrl(
                evt.coordinate,
                viewResolution,
                map.getView().getProjection(),
                { INFO_FORMAT: 'application/json' }
              );
            if (url) {
              var responseJSON = syncFetch(url).json();
              if (
                responseJSON.features &&
                responseJSON.features.length > 0 &&
                responseJSON.features[0].properties
              ) {
                let filter =
                  this.props.valueRanges[this.props.getItemKey(item)];

                let in_filter = true;
                if (filter !== undefined) {
                  let feature = responseJSON.features[0].properties;

                  if (item.data_type == 'raster') {
                    let pixel_type = Object.keys(feature)[0];
                    let pixel_value = feature[pixel_type];

                    if (pixel_value < filter[0] || pixel_value > filter[1]) {
                      in_filter = false;
                    }
                  } else {
                    in_filter = true;
                  }
                }
                if (in_filter) {
                  feature_attributes.push({
                    url: urls.toString(),
                    layer_name,
                    features: responseJSON.features[0].properties,
                  });
                  thisObject.setState({
                    feature_attributes,
                  });
                  thisObject.popup.setPosition(evt.coordinate);
                }
              }
            }
          }
        }
      }

      if (feature_attributes.length == 0) {
        this.handleClose();
        document.getElementById('popup-closer').click();
      }
    });
  }

  componentDidUpdate() {
    if (
      this.props.selectedDatasets.length !== this.state.selectedDatasets.length
    ) {
      this.setState({
        selectedDatasets: [...this.props.selectedDatasets],
      });
    }
  }

  getValueRange(item) {
    for (const [key, value] of Object.entries(this.props.valueRanges)) {
      if (
        item.gis_services[0].service_url +
          '/' +
          item.gis_services[0].layer_name_name ===
        key
      ) {
        return value;
      }
    }

    const layerConfig = this.getLayerConfig(item.url, item.layer_name);
    if (layerConfig.type === 'raster') {
      return [layerConfig.min, layerConfig.max];
    }
    return [-10000, 10000];
  }

  getLayerConfig(url, layer) {
    for (let i = 0; i < this.state.selectedDatasets.length; i++) {
      let dataset = this.state.selectedDatasets[i];
      if (
        dataset.mapping[0].layer === layer &&
        dataset.mapping[0].url === url
      ) {
        return dataset.mapping[0];
      }
    }
    return null;
  }

  // One tab per clicked layer that has something to show.
  getLayerTabs() {
    return [...this.state.feature_attributes]
      .sort(compareLayers)
      .map((item) => {
        const dataset = this.state.selectedDatasets.find(
          (e) => this.props.getItemKey(e) === item.url + '/' + item.layer_name
        );
        if (!dataset) return null;
        const gisData = this.props.getGisService(dataset);
        if (
          gisData.data_type !== 'raster' &&
          gisData.data_type !== 'vector' &&
          gisData.data_type !== 'tile'
        ) {
          return null;
        }
        const rows = buildRows(item.features, item.fieldInfo);
        if (rows.length === 0) return null;
        return {
          key: item.url + '/' + item.layer_name,
          title: formatTitle(dataset.title),
          isRaster:
            gisData.data_type === 'raster' || gisData.data_type === 'tile',
          isEstimate: !!item.isEstimate,
          rows,
        };
      })
      .filter(Boolean);
  }

  renderRows(tab) {
    // A single raster value reads better as one big number than a table.
    if (tab.isRaster && tab.rows.length === 1) {
      const [row] = tab.rows;
      return (
        <div className="popup-single-value">
          {row.noData ? (
            <span className="popup-single-value-empty">
              This layer has no data at this point.
            </span>
          ) : (
            <>
              <span className="popup-single-value-label">{row.label}</span>
              <span className="popup-single-value-number">{row.value}</span>
              {tab.isEstimate && (
                <span className="popup-single-value-note">
                  Estimated from the map color
                </span>
              )}
            </>
          )}
        </div>
      );
    }
    const hiddenCount = this.state.showAllRows
      ? 0
      : Math.max(0, tab.rows.length - COLLAPSED_ROW_COUNT);
    const rows = hiddenCount
      ? tab.rows.slice(0, COLLAPSED_ROW_COUNT)
      : tab.rows;
    return (
      <>
        <dl className="popup-fields">
          {rows.map((row, i) => (
            <div className="popup-field" key={row.label + i}>
              <dt>{row.label}</dt>
              <dd>
                {row.isLink ? (
                  <a href={row.value} target="_blank" rel="noopener noreferrer">
                    Open link
                  </a>
                ) : (
                  row.value
                )}
              </dd>
            </div>
          ))}
        </dl>
        {tab.rows.length > COLLAPSED_ROW_COUNT && (
          <button
            type="button"
            className="popup-show-more"
            aria-expanded={this.state.showAllRows}
            onClick={() =>
              this.setState(
                (state) => ({ showAllRows: !state.showAllRows }),
                this.keepPopupInView
              )
            }
          >
            {hiddenCount
              ? `Show ${hiddenCount} more fields`
              : 'Show fewer fields'}
          </button>
        )}
      </>
    );
  }

  render() {
    const tabs = this.getLayerTabs();
    const activeTab = Math.min(this.state.activeTab, tabs.length - 1);
    const current = tabs[activeTab];

    if (tabs.length === 0 && this.popup) {
      this.popup.setPosition(undefined);
    }

    return (
      <div>
        <div
          id="popup"
          ref={this.popupRef}
          className="ol-popup"
          onKeyDown={this.handleEscape}
        >
          <div className="popup-header">
            <div>
              <span className="popup-header-title">
                {tabs.length > 1
                  ? `${tabs.length} layers here`
                  : 'Layer details'}
              </span>
              {this.state.clickedLonLat && (
                <span className="popup-header-location">
                  {formatLonLat(this.state.clickedLonLat)}
                </span>
              )}
            </div>
            <button
              type="button"
              id="popup-closer"
              className="ol-popup-closer"
              aria-label="Close layer details"
              onClick={this.handleClose}
            >
              ×
            </button>
          </div>
          {tabs.length > 1 && (
            <div
              className="popup-tabs"
              role="tablist"
              aria-label="Layers at this point"
            >
              {tabs.map((tab, i) => (
                <button
                  type="button"
                  key={tab.key}
                  id={`popup-tab-${i}`}
                  role="tab"
                  aria-selected={i === activeTab}
                  aria-controls="popup-content"
                  tabIndex={i === activeTab ? 0 : -1}
                  className={
                    'popup-tab' + (i === activeTab ? ' popup-tab-active' : '')
                  }
                  title={tab.title}
                  onClick={() => this.selectTab(i)}
                  onKeyDown={(e) => this.handleTabKeyDown(e, tabs.length)}
                >
                  {tab.title}
                </button>
              ))}
            </div>
          )}
          <div
            id="popup-content"
            className="popup-content"
            {...(tabs.length > 1 && {
              role: 'tabpanel',
              'aria-labelledby': `popup-tab-${activeTab}`,
            })}
          >
            {current && (
              <>
                <h3 className="popup-layer-title">
                  {current.title}
                  <span className="popup-layer-type">
                    {current.isRaster ? 'Raster' : 'Vector'}
                  </span>
                </h3>
                {this.renderRows(current)}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ForestDataMapPopover;
